import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  Tooltip,
  Avatar,
  Button,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { blue } from '@mui/material/colors'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FIELD_TYPES, FORMATS, PROCESS_ACTIVITY_STATUSES } from '../../utility/constants'
import { Box } from '@mui/system'
import { normalizeExtractedValues } from '../../utility/normalizeExtractedValues'
import { useEffect, useState } from 'react'
import ConfirmationBox from './ConfirmationBox'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import DataPreview from '../DataPreview/DataPreview'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'

import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined'

import LockingTooltip from '../LockingTooltip'
import { fileServices } from '../../services/fileServices'
import { useAuthContext } from '../../hooks/useAuthContext'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'

import StandaloneFilePreview from './PreviewComponent/components/StandaloneFilePreview'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import TaskKebabMenu from './TaskKebabMenu'

const useStyles = makeStyles({
  avatar: {
    backgroundColor: () => {
      return blue[500]
    },
  },
})

export default function TaskCard({
  task,
  deleteTask,
  setIsDelegateOpen,
  setTask,
  setProcessReferenceNumber,
  teamDetails,
  visibleData,
}) {
  const classes = useStyles()
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [filePreview, setFilePreview] = useState(null)
  const [efficiency, setEfficiency] = useState()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [openMenuIndex, setOpenMenuIndex] = useState(null)

  const confirmationText = `Are you sure you want to delete ${task.process_reference.toUpperCase()} task?`
  const { cardIcons, datetitle, cardtitle, cardSubtitle } = useResponsiveTypography()

  const [selectedPreviewProcessId, setSelectedPreviewProcessId] = useState(null)

  const downloadFile = async (fileKey) => {
    try {
      await fileServices.downloadFile(user, fileKey)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  useEffect(() => {
    const startTime = moment(new Date(task.process_data_created_at)).format(FORMATS.DATETIME)
    const endTime = moment(new Date(task.process_data_modified_at)).format(FORMATS.DATETIME)

    const expectedTime = task.expected_minutes_to_complete

    const totalTimeTaken = moment.duration(moment(endTime).diff(moment(startTime)))
    let timeTakenindays = totalTimeTaken.days()
    let timeTakeninhours = totalTimeTaken.hours()
    let timeTakeninminutes = totalTimeTaken.minutes()

    let totalTimeTakenInMinutes
    if (timeTakeninminutes < 1) {
      totalTimeTakenInMinutes = 1
    } else {
      totalTimeTakenInMinutes = timeTakenindays * 1440 + timeTakeninhours * 60 + timeTakeninminutes
    }

    setEfficiency(Math.round((expectedTime * 100) / totalTimeTakenInMinutes))
  }, [task])

  const openDeleteDialog = () => {
    setIsDialogOpen(true)
  }

  const openDelegateDialog = (task) => {
    setTask(task)
    setProcessReferenceNumber(task.process_reference)
    setIsDelegateOpen(true)
  }

  const matchingData = visibleData?.find((v) => v.process_id === task.process_id)
  const extractedValues = matchingData?.extracted_values || []

  return (
    <>
      <section className="task-card">
        <Card
          className="blog-box"
          elevation={1}
          sx={{
            ':hover': {
              boxShadow: 0,
            },

            boxShadow: 0,
          }}
        >
          <div className="task-groupicon">
            <Box sx={{ ...cardIcons, display: 'flex', justifyContent: 'flex-end' }}>
              <TaskKebabMenu
                task={task}
                openDeleteDialog={openDeleteDialog}
                openDelegateDialog={openDelegateDialog}
                onPreviewClick={setSelectedPreviewProcessId}
                teamDetails={teamDetails}
              />
            </Box>
          </div>

          <div className="task-card-header">
            <CardHeader
              sx={{ padding: '0px 0px' }}
              avatar={
                <Avatar
                  className={classes.avatar}
                  sx={{
                    bgcolor: task.color_code,
                    width: '30px',
                    height: '30px',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                >
                  {task.process_reference[0].toUpperCase()}
                </Avatar>
              }
              title={
                <Typography
                  className="refrence-number "
                  style={{ fontSize: cardtitle, color: '#27104E', fontWeight: '700' }}
                >
                  {task.process_reference}
                </Typography>
              }
              subheader={
                <Typography
                  className="taskprocess-name text-dotted"
                  style={{ fontSize: cardSubtitle, color: '#4B4F60', fontWeight: '500' }}
                >
                  {task.process_template_name}
                </Typography>
              }
            />

            {isDialogOpen && (
              <ConfirmationBox
                title={confirmationText}
                isDialogOpen={isDialogOpen}
                setIsDialogOpen={setIsDialogOpen}
                onYes={() => deleteTask(task)}
              />
            )}
          </div>

          <div className="task-card-content">
            <CardContent sx={{ padding: '0px 0px 0px 0px !important' }}>
              <Typography
                className="mb-1"
                variant="body2"
                sx={{ fontSize: cardtitle, lineHeight: '10px' }}
                color="textSecondary"
              >
                <span
                  style={{
                    color: '#27104E',
                    fontWeight: '600',
                  }}
                >{`${task.activity_template_name} (version ${task.process_version}) `}</span>
              </Typography>

              {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.CLOSED && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ElectricBoltOutlinedIcon color="info" sx={{ fontSize: '14px' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: datetitle }}
                    color="textSecondary"
                    className="textSecondary fontWeight500"
                  >
                    {efficiency > 100 ? <> Greater than 100% </> : <>{efficiency}%</>}
                  </Typography>
                </Box>
              )}
              {extractedValues.map((item, index) => {
                const { key, value, dataStructure } = item

                let displayContent
                let tooltipContent

                // Detect timestamped_bigtext either by dataStructure or by value shape
                const isTimestampedBigText =
                  (dataStructure === FIELD_TYPES.TIMESTAMPED_BIGTEXT && Array.isArray(value)) ||
                  (dataStructure === FIELD_TYPES.LIST &&
                    Array.isArray(value) &&
                    value.length > 0 &&
                    typeof value[0] === 'object' &&
                    'text' in value[0] &&
                    'timestamp' in value[0])

                if (isTimestampedBigText) {
                  displayContent =
                    value.length > 0 ? (
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexDirection: 'column' }}
                      >
                        {value.slice(0, 3).map((entry, i) => (
                          <span key={i}>
                            <strong>{`${entry.timestamp}:`}</strong> {entry.text}
                          </span>
                        ))}
                        {value.length > 3 && <span>...</span>}
                      </Box>
                    ) : (
                      '--'
                    )

                  tooltipContent = (
                    <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                      {value.map((entry, i) => (
                        <span key={i}>
                          <strong>{`${entry.timestamp}:`}</strong> {entry.text}
                        </span>
                      ))}
                    </Box>
                  )
                } else if (dataStructure === FIELD_TYPES.LIST_MULTI) {
                  const hasMore = value.length > 3
                  const visibleValues = value.slice(0, 3)
                  displayContent =
                    value.length > 0 ? (
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexDirection: 'column' }}
                      >
                        {visibleValues.map((val, i) => (
                          <strong key={i}>{val}</strong>
                        ))}
                        {hasMore && <span>...</span>}
                      </Box>
                    ) : (
                      '--'
                    )
                  tooltipContent = (
                    <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                      {value.map((val, i) => (
                        <span key={i}>{val}</span>
                      ))}
                    </Box>
                  )
                } else if (dataStructure === FIELD_TYPES.LIST) {
                  if (Array.isArray(value)) {
                    if (value.length === 0) {
                      displayContent = '--'
                      tooltipContent = '--'
                    } else {
                      const formattedValues = value.map((val) => {
                        if (typeof val === 'object' && val !== null) {
                          if (val.first_name || val.last_name) {
                            return `${val.first_name || ''} ${val.last_name || ''}`.trim()
                          }
                          if (val.name) return val.name
                          if (val.value && val.value.includes('@')) {
                            return val.value.replace(/\(.*?\)/, '').trim()
                          }
                          return val.value || '--'
                        } else if (typeof val === 'string') {
                          return val.replace(/\S+@\S+\.\S+/, '').trim()
                        }
                        return val
                      })
                      const hasMore = formattedValues.length > 3
                      const visibleValues = formattedValues.slice(0, 3)
                      displayContent = (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            lineHeight: '1.0',
                            gap: '6px', // space between each name
                          }}
                        >
                          {visibleValues.map((val, i) => (
                            <strong key={i}>{val}</strong>
                          ))}
                          {hasMore && <span>...</span>}
                        </Box>
                      )
                      tooltipContent = (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            lineHeight: '1.2',
                            gap: '6px',
                          }}
                        >
                          {formattedValues.map((val, i) => (
                            <span key={i}>{val}</span>
                          ))}
                        </Box>
                      )
                    }
                  }

                  // STRING or NUMBER HANDLING
                  else if (typeof value === 'string' || typeof value === 'number') {
                    const isLikelyDate =
                      moment(value, moment.ISO_8601, true).isValid() ||
                      moment(value, 'YYYY-MM-DD HH:mm:ss', true).isValid()
                    let formattedValue = isLikelyDate ? moment(value).format('DD MMM YYYY') : value
                    if (
                      !isLikelyDate &&
                      typeof formattedValue === 'string' &&
                      formattedValue.includes(',')
                    ) {
                      const items = formattedValue
                        .split(',')
                        .map((item) => item.trim())
                        .filter((item) => item.length > 0)
                      formattedValue = (
                        <div style={{ lineHeight: '1.4', marginBottom: '4px' }}>
                          {items.map((item, index) => (
                            <div key={index}>{item}</div>
                          ))}
                        </div>
                      )
                      tooltipContent = formattedValue
                      displayContent = <strong>{formattedValue}</strong>
                    } else {
                      displayContent = <strong>{formattedValue}</strong>
                      tooltipContent = formattedValue
                    }
                  } else {
                    displayContent = '--'
                    tooltipContent = '--'
                  }
                } else if (dataStructure === FIELD_TYPES.FILE && value) {
                  displayContent = (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 'calc(100% - 30px)',
                          flexGrow: 1,
                        }}
                      >
                        <strong>{value}</strong>
                      </Typography>
                    </Box>
                  )
                  tooltipContent = value
                } else {
                  // fallback for other cases, including empty or null values
                  let safeValue
                  if (
                    value === null ||
                    value === undefined ||
                    (typeof value === 'string' && value.trim() === '') ||
                    (typeof value === 'object' && Object.keys(value).length === 0)
                  ) {
                    safeValue = '--'
                  } else if (typeof value === 'object') {
                    if ('value' in value && typeof value.value === 'string') {
                      safeValue = value.value
                    } else {
                      safeValue = '--'
                    }
                  } else {
                    safeValue = value
                  }

                  const isLikelyDate =
                    key.toLowerCase().includes('date') &&
                    moment(value, moment.ISO_8601, true).isValid()

                  const displayValue = isLikelyDate
                    ? moment(value).format(FORMATS.DATE)
                    : Array.isArray(safeValue)
                    ? safeValue.slice(0, 3).join(', ') + (safeValue.length > 3 ? ', ...' : '')
                    : safeValue ?? '--'

                  tooltipContent = isLikelyDate
                    ? moment(value).format(FORMATS.DATE)
                    : Array.isArray(safeValue)
                    ? safeValue.join(', ')
                    : safeValue ?? '--'
                  displayContent = <strong>{displayValue}</strong>
                }

                return (
                  <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
                    <LockingTooltip
                      title={tooltipContent}
                      arrow
                      open={openMenuIndex === index ? false : undefined}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '9px',
                            fontWeight: '500',
                          },
                        },
                      }}
                    >
                      <Typography component="span" className="task-subtext text-ellipsis">
                        <span>{key}:</span> {displayContent}
                      </Typography>
                    </LockingTooltip>
                    {dataStructure === FIELD_TYPES.FILE && (
                      <LongMenu
                        options={[
                          {
                            label: 'Preview',
                            icon: (
                              <IconButton size="small" sx={{ padding: '0px 4px' }}>
                                <RemoveRedEyeOutlinedIcon fontSize="18px" />
                              </IconButton>
                            ),
                            onClick: () => {
                              setOpenMenuIndex(null)
                              setFilePreview(value)
                            },
                          },
                          {
                            label: 'Download',
                            icon: (
                              <IconButton size="small" sx={{ padding: '0px 4px' }}>
                                <FileDownloadOutlinedIcon fontSize="18px" />
                              </IconButton>
                            ),
                            onClick: () => {
                              setOpenMenuIndex(null)
                              downloadFile(value)
                            },
                          },
                        ]}
                        onOpen={() => setOpenMenuIndex(index)}
                        onClose={() => setOpenMenuIndex(null)}
                      />
                    )}
                  </div>
                )
              })}
              <Box
                className="mb-0"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'end',
                  margin: '0px 0px 0px 0px',
                }}
              >
                {/* LEFT: Calendar Details */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CalendarMonthOutlinedIcon color="info" sx={{ fontSize: '14px' }} />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: datetitle }}
                    color="textSecondary"
                    className="textSecondary fontWeight600"
                  >
                    {moment(new Date(task.process_data_created_at)).format(FORMATS.DATETIME)}
                  </Typography>

                  {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.CLOSED && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          height: '12px',
                          borderLeft: '1px solid #656D92',
                          marginLeft: '6px',
                          marginRight: '6px',
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: datetitle }}
                        color="textSecondary"
                        className="textSecondary fontWeight600"
                      >
                        {moment(new Date(task.process_data_modified_at)).format(FORMATS.DATETIME)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {task.process_activity_status === PROCESS_ACTIVITY_STATUSES.ACTIVE && (
                  <Tooltip
                    title={
                      {
                        'data-entry': 'Fill Data',
                        review: 'Review',
                        'long-running':
                          task.process_data_modified_by === user.id ? 'Fill Data' : 'Review',
                      }[task.activity_type] || 'Work On Task'
                    }
                  >
                    <Button
                      variant="contained"
                      sx={{
                        border: '1px solid #6C47E4',
                        color: 'primary.main',
                        backgroundColor: 'transparent',
                        fontWeight: '700 !important',
                        fontSize: {
                          lg: '9px !important',
                          sm: '8px !important',
                          xs: '8px !important',
                        },
                        padding: {
                          xs: '4px 6px',
                          lg: '4px 6px',
                        },
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          border: '1px solid #6C47E4',
                          boxShadow: 'none',
                          color: '#fff',
                        },
                      }}
                      onClick={() =>
                        navigate(`/process-data/${task.process_id}/${task.activity_template_id}`)
                      }
                    >
                      {{
                        'data-entry': 'Fill Data',
                        review: 'Review',
                        'long-running':
                          task.process_data_modified_by === user.id ? 'Fill Data' : 'Review',
                      }[task.activity_type] || 'Work On Task'}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </div>
        </Card>
      </section>

      {selectedPreviewProcessId ? (
        <DataPreview
          onClose={() => setSelectedPreviewProcessId(null)}
          processId={selectedPreviewProcessId}
          open={!!selectedPreviewProcessId}
        />
      ) : null}
      <StandaloneFilePreview
        fileKey={filePreview}
        open={!!filePreview}
        onClose={() => setFilePreview(null)}
      />
    </>
  )
}
